import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {LegacyPlaidConfig, NgxPlaidLinkService, PlaidErrorMetadata, PlaidErrorObject, PlaidEventMetadata, PlaidLinkHandler, PlaidSuccessMetadata} from 'ngx-plaid-link';
import {BehaviorSubject, Observable, catchError, filter, from, of, switchMap, take, tap, throwError} from 'rxjs';
import {AppSettingsService} from 'src/app/services/app-settings.service';
import {PlaidDataService} from 'src/app/services/shared/plaid-data.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {StorageService} from 'src/app/services/storage.service';
import {UtilsService} from 'src/app/services/utils.service';
import {LoggerService} from 'src/app/shared/errors/logger.service';
import {AccessTokenCreateDto} from 'src/app/shared/models/cre-banking/plaid/AccessTokenCreateDto.model';
import {BankAccountSyncDetailsDto} from './models/bankAccountSyncDetailsDto.model';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {AbstractRestService} from 'src/app/shared/types/AbstractRestService';
import {HttpService} from 'src/app/services/http.service';
import {LinkTokenReadDto} from 'src/app/shared/models/cre-banking/plaid/LinkTokenReadDto.model';
import {SnackbarMessageComponent, SnackbarMessageParams} from 'src/app/shared/components/snackbar-message/snackbar-message.component';
import {BankAccountsMsService} from './bank-accounts-ms.service';
import {RoutingService} from 'src/app/services/routing.service';
import {PlaidTokenCreationMode} from 'src/app/shared/enums/PlaidTokenCreationMode.enum';

@Injectable({
  providedIn: 'root'
})
export class LinkedBankAccountService extends AbstractRestService<any, any> {

  protected readonly baseEndpoint$ = this.appSettingsService.localEnv ?
    of('https://localhost:44360/api/plaid/') :
    of('plaid/');

  readonly plaidKnowErrors = new Set(['OAUTH_STATE_ID_ALREADY_PROCESSED']);
  readonly openOAuthEventName = 'OPEN_OAUTH';

  private _isRefreshingLinkToken$ = new BehaviorSubject<boolean>(false);
  isRefreshingLinkToken$ = this._isRefreshingLinkToken$.asObservable();

  private _isRefreshingLinkedPlaidBankAccounts$ = new BehaviorSubject<boolean>(false);
  isRefreshingLinkedPlaidBankAccounts$ = this._isRefreshingLinkedPlaidBankAccounts$.asObservable();

  private _routingToBankAccountScreen: boolean = false;
  private _holdingId: number = null;

  constructor(
    public http: HttpService,
    private plaidLinkService: NgxPlaidLinkService,
    private router: Router,
    private routingService: RoutingService,
    private appSettingsService: AppSettingsService,
    private utilsService: UtilsService,
    private snackbarService: SnackbarService,
    private logger: LoggerService,
    private bankAccountsMsService: BankAccountsMsService) {
    super(http);
  }

  /**
   * Exchange public token with access token
   * @param requestData request data for exchanging the public token
   */
  public exchangePublicToken(requestData: AccessTokenCreateDto): Observable<BankAccountSyncDetailsDto | BaseResponseDto> {
    if (!requestData) {
      return of(null);
    }

    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.postTyped<BankAccountSyncDetailsDto | BaseResponseDto>(`${baseUrl}exchange`, requestData)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public disconnectLinkedBankAccounts(clientBankAccountId: number): Observable<boolean | BaseResponseDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.putTyped<boolean | BaseResponseDto>(`${baseEndpoint}disconnect/${clientBankAccountId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    )
  }

  public reconnectLinkedBankAccounts(clientBankAccountId: number): Observable<boolean | BaseResponseDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.putTyped<boolean | BaseResponseDto>(`${baseEndpoint}reconnect/${clientBankAccountId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    )
  }

  /**
   * Get a link token to init link process
   */
  public getLinkToken(clientBankAccountId: number = null): Observable<LinkTokenReadDto | BaseResponseDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<LinkTokenReadDto | BaseResponseDto>(`${baseUrl}link-token${!!clientBankAccountId ? ('/' + clientBankAccountId) : ''}?creationMode=${PlaidTokenCreationMode.ExternalAccounts}`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  /**
   * Opens Plaid link GUI
   */
  public openPlaidLink(clientBankAccountId: number = null, routingToBankAccountScreen: boolean = false, holdingId: number = null) {
    this._isRefreshingLinkToken$.next(true);
    this._routingToBankAccountScreen = routingToBankAccountScreen;
    this.getLinkToken(clientBankAccountId).pipe(
      take(1),
      switchMap(linkTokenObject => this.getPlaidLinkHandler((linkTokenObject as LinkTokenReadDto).linkToken,
        this.onPlaidLinkSuccess.bind(this),
        this.onPlaidLinkExit.bind(this),
        this.onPlaidLinkEvent.bind(this))),
      tap(_ => this._isRefreshingLinkToken$.next(false)),
      catchError(err => {
        this._isRefreshingLinkToken$.next(false);
        return throwError(() => err)
      })
    ).subscribe(handler => {
      handler.open();
    });
  }


  // ############## PRIVATE METHOD ####################

  private getPlaidLinkHandler(linkToken: string, onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => any,
    onExit: (error: PlaidErrorObject, metadata: PlaidErrorMetadata) => any,
    onEvent: (eventName: string, metadata: PlaidEventMetadata) => any,
    receivedRedirectUri: string = null): Observable<PlaidLinkHandler> {
    let plaidConfig: LegacyPlaidConfig = {
      apiVersion: 'v2',
      env: this.appSettingsService.plaidEnvironment,
      token: linkToken,
      product: ['auth'],
      onSuccess,
      onExit,
      onEvent
    };
    if (!!receivedRedirectUri) {
      plaidConfig = Object.assign({}, plaidConfig, {receivedRedirectUri});
    }

    const plaidHandlerPromise = this.plaidLinkService.createPlaid(plaidConfig);

    return from(plaidHandlerPromise);
  }

  /**
   * Callback to be used when the user successfully linked a bank account using Plaid
   * @param publicToken Public token received from plaid
   * @param metadata Additional data
   */
  private onPlaidLinkSuccess(publicToken: string, metadata: PlaidSuccessMetadata) {
    const exchangeRequestData = new AccessTokenCreateDto();
    exchangeRequestData.publicToken = publicToken;
    this.exchangePublicToken(exchangeRequestData).pipe(take(1)).subscribe({
      next: res => {

        this.refreshLinkedBankAccounts();
        if ('addedSuccessfully' in res && res.addedSuccessfully) {
          this.snackbarService.showSnackbarFromComponent(SnackbarMessageComponent,
            new SnackbarMessageParams(`${res.addedSuccessfully} external accounts successfully added`));
        }
        if (this._routingToBankAccountScreen) {
          this.router.navigate([this.routingService.bankAccountsPage("gp")]);
        }
        else {
          this.bankAccountsMsService.bankAccountRefresh(true);
        }
        this._routingToBankAccountScreen = false;
      },
      error: error => {
        if ('responseStatus' in error) {
          this.utilsService.alertErrorMessage(error);
        } else {
          this.logger.error('Error exchanging public token', error);
        }
      }
    });
  }

  /**
   * Callback to be used when the user exists Plaid link
   * @param error Error if exists
   * @param metadata Additional data
   */
  private onPlaidLinkExit(error: PlaidErrorObject, metadata: PlaidErrorMetadata) {
    if (!!error) {
      if (!this.plaidKnowErrors.has(error.error_code)) {
        this.logger.error('Plaid link closed with an error', error, metadata);
      }
      this.openPlaidLink();
    }
  }

  /**
   * Callback to be used on event from plaid link
   * @param eventName Name of the event
   * @param metadata Additional data
   */
  private onPlaidLinkEvent(eventName: string, metadata: PlaidEventMetadata) {
    if (eventName === this.openOAuthEventName) {
      // this.saveOAuthInformation(0, this.paymentToken);
    }
  }

  private refreshLinkedBankAccounts() {
    this.bankAccountsMsService.bankAccountCountsRefresh();
  }

}
